import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Crown, KeyRound, ShieldAlert, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { loadPlatform } from "@/lib/platform";

const field = "w-full rounded-lg border border-[#D4AF37]/30 bg-black px-4 py-3 text-white tracking-[0.2em] focus:border-[#FFD86A] focus:outline-none";

export function MfaGate() {
  const { checkAuth, logout } = useAuth();
  const [code, setCode] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/mfa/verify", { code }); await checkAuth(); toast.success("Verified"); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div data-testid="mfa-gate" className="flex min-h-screen items-center justify-center bg-[#050505] px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl border border-[#D4AF37]/40 bg-[#0B0B0D] p-8">
        <Crown className="h-9 w-9 text-[#D4AF37]" />
        <h1 className="font-display text-2xl gold-text">Two-factor verification</h1>
        <p className="text-sm text-white/60">Enter the 6-digit code from your authenticator app, or a recovery code.</p>
        <input data-testid="mfa-code-input" autoFocus inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={field} />
        <button data-testid="mfa-submit" className="btn-gold w-full py-3 text-sm">Verify</button>
        <button type="button" data-testid="mfa-logout" onClick={logout} className="w-full text-xs text-white/50 hover:text-white">Sign out</button>
      </form>
    </div>
  );
}

export function MaintenancePage() {
  return (
    <div data-testid="maintenance-page" className="flex min-h-screen flex-col items-center justify-center bg-[#050505] px-6 text-center">
      <Wrench className="h-12 w-12 text-[#D4AF37]" strokeWidth={1.3} />
      <h1 className="mt-6 font-display text-4xl font-bold gold-text">AETRYX is polishing the crown</h1>
      <p className="mt-3 max-w-md text-white/60">We're performing scheduled maintenance. Streaming will be back shortly — thank you for your patience.</p>
      <p data-testid="maintenance-autocheck" className="mt-2 text-xs text-white/35">This page checks automatically and will reopen AETRYX as soon as we're back.</p>
      <button data-testid="maintenance-retry" onClick={() => loadPlatform(true)} className="btn-ghost-gold mt-8 px-6 py-3 text-sm">Check now</button>
    </div>
  );
}

export function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [inv, setInv] = useState(null);
  const [f, setF] = useState({ name: "", password: "" });
  useEffect(() => { api.get(`/auth/invite/${token}`).then((r) => setInv(r.data)).catch(() => setInv(false)); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/auth/invite/${token}/accept`, f);
      localStorage.setItem("aetryx_token", data.access_token); localStorage.setItem("aetryx_refresh", data.refresh_token);
      await checkAuth(); navigate("/profiles");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div data-testid="invite-page" className="flex min-h-screen items-center justify-center bg-[#050505] px-4">
      <div className="w-full max-w-md rounded-2xl border border-[#D4AF37]/40 bg-[#0B0B0D] p-8">
        <Crown className="h-9 w-9 text-[#D4AF37]" />
        {inv === null ? <p className="mt-4 text-white/50">Checking invite…</p> : !inv ? <p data-testid="invite-invalid" className="mt-4 text-white/70">This invite is no longer valid. <Link to="/login" className="text-[#FFD86A]">Sign in</Link></p> : (
          <form onSubmit={submit} className="mt-4 space-y-4">
            <h1 className="font-display text-2xl gold-text">Join the AETRYX admin team</h1>
            <p className="text-sm text-white/60">{inv.email} · permissions: {inv.perms.join(", ") || "none"}</p>
            <input data-testid="invite-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className={field.replace("tracking-[0.2em]", "")} />
            <input data-testid="invite-password" type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Choose a password (8+ characters)" className={field.replace("tracking-[0.2em]", "")} />
            <button data-testid="invite-accept" className="btn-gold w-full py-3 text-sm">Accept & create account</button>
          </form>
        )}
      </div>
    </div>
  );
}

export const RecoveryCodes = ({ codes }) => {
  const dl = () => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([`AETRYX recovery codes\n\n${codes.join("\n")}\n`], { type: "text/plain" })); a.download = "aetryx-recovery-codes.txt"; a.click(); };
  return (
    <div data-testid="recovery-codes" className="space-y-3 rounded-xl border border-[#FFD86A]/40 bg-black p-4">
      <p className="text-sm text-[#FFD86A]">Save these one-time recovery codes now — they won't be shown again.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm text-white">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <button data-testid="recovery-download" onClick={dl} className="btn-ghost-gold px-4 py-2 text-xs">Download codes</button>
    </div>
  );
};

const StepUpCtx = createContext(null);
export const useStepUp = () => useContext(StepUpCtx);

export function StepUpProvider({ children }) {
  const tok = useRef({ v: "", exp: 0 });
  const [ask, setAsk] = useState(null);
  const [code, setCode] = useState("");
  const getToken = () => new Promise((resolve, reject) => setAsk({ resolve, reject }));
  const run = useCallback(async (fn) => {
    const call = () => fn({ headers: tok.current.exp > Date.now() ? { "X-Step-Up": tok.current.v } : {} });
    try { return await call(); } catch (e) {
      if (e?.response?.data?.detail?.code !== "step_up_required") throw e;
      await getToken();
      return call();
    }
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/step-up", { code });
      tok.current = { v: data.step_up_token, exp: Date.now() + (data.expires_in - 15) * 1000 };
      setCode(""); ask.resolve(); setAsk(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <StepUpCtx.Provider value={run}>
      {children}
      <Dialog open={Boolean(ask)} onOpenChange={(o) => { if (!o) { ask?.reject(new Error("Cancelled")); setAsk(null); } }}>
        <DialogContent data-testid="stepup-dialog" className="max-w-sm border border-[#D4AF37]/40 bg-[#0B0B0D] text-white">
          <DialogTitle className="flex items-center gap-2 font-display text-xl gold-text"><ShieldAlert className="h-5 w-5" /> Confirm it's you</DialogTitle>
          <form onSubmit={submit} className="space-y-3">
            <p className="text-sm text-white/60">Sensitive action — enter a fresh authenticator code. Valid for 5 minutes.</p>
            <input data-testid="stepup-code" autoFocus inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={field} />
            <button data-testid="stepup-submit" className="btn-gold w-full py-3 text-sm"><KeyRound className="h-4 w-4" /> Confirm</button>
          </form>
        </DialogContent>
      </Dialog>
    </StepUpCtx.Provider>
  );
}
